import { useMutation } from 'react-query';
import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useDebouncedCallback } from 'beautiful-react-hooks';

import TestimonialContext from '~/App/Modules/Testimonials/Context/TestimonialContext';
import useStepContext from '~/App/Components/Typeform/useStepContext';
import RespondentContext from '../Context/Respondent';
import RequestFilesItem from '../Components/RequestFilesItem';
import RequestLinksItem from '../Components/RequestLinksItem';
import { Map, List } from 'immutable';
import { Request } from '../Records';
import useTypeformNavContext from '~/Share/TestimonialLandingPage/Context/TypeformNav';

import {
  updateRespondentPosition,
  UpdateRespondentPositionParams,
} from '../Api/TestimonialRespondent';

import useAnalytics from '~/react-hooks/use-analytics';
import { TESTIMONIAL_RESPONSE_FORM_NEXT } from '~/generated/AnalyticsTypes';
import { FileRequestType, LinkRequestType } from '~/types/models/testimonial';
import AttachmentCardButtons from '../Components/AttachmentCardButtons';
import RequestPositionField from '../Components/RequestPositionField';
import AttachmentCard from '../Components/AttachmenCard';
import { useTranslation } from 'react-i18next';
import { useFeatures } from '~/react-hooks/use-features';
import { getTestimonialLogoUrl } from '~/App/Modules/Testimonials/util';
import { computeTextColor } from '@vimily/bonjoro-ui-components-v2';

export interface AttachmentCardContainerProps {
  showPositionField?: boolean;
  requests: Map<'files' | 'links', List<Request>>;
  onPreviousClick?: () => void;
  onNextClick?: () => void;
  className?: string;
  disableFileUpload?: boolean;
  emptyStateCopy?: string;
}

const hasRequest = (
  list: List<Request>,
  type: FileRequestType | LinkRequestType
): boolean => !!list.find((request) => request?.get('type') === type);

interface TestimonialResponseFormRequests {
  official_title: boolean;
  company_logo: boolean;
  profile_photo: boolean;
  facebook_link: boolean;
  linkedin_link: boolean;
  twitter_link: boolean;
  website_link: boolean;
  others: string[];
}

const mapRequestsForTracking = (
  requests: AttachmentCardContainerProps['requests']
): Omit<TestimonialResponseFormRequests, 'official_title'> => {
  const files = requests.get('files');
  const links = requests.get('links');

  const others = [...files.toArray(), ...links.toArray()]
    .filter((item) =>
      [FileRequestType.CustomFile, LinkRequestType.CustomLink].includes(
        item?.get('type')
      )
    )
    .map((item) => item.get('label'));

  return {
    company_logo: hasRequest(files, FileRequestType.CompanyLogo),
    profile_photo: hasRequest(files, FileRequestType.ProfilePhoto),
    facebook_link: hasRequest(links, LinkRequestType.Facebook),
    linkedin_link: hasRequest(links, LinkRequestType.LinkedIn),
    twitter_link: hasRequest(links, LinkRequestType.Twitter),
    website_link: hasRequest(links, LinkRequestType.Website),
    others,
  };
};

export default function AttachmentCardContainer({
  showPositionField,
  className,
  requests,
  onPreviousClick,
  onNextClick,
  disableFileUpload = false,
  emptyStateCopy,
}: AttachmentCardContainerProps) {
  const { respondent } = useContext(RespondentContext);
  const testimonial = useContext(TestimonialContext);
  const { nextStep, previousStep, isCurrent } = useStepContext();
  const { setNavBarProps } = useTypeformNavContext();
  const analytics = useAnalytics();

  const { t } = useTranslation();

  const [position, setPosition] = useState<string>(respondent.get('position'));
  const fileList = requests.get('files');
  const linkList = requests.get('links');
  const respondentUuid = respondent.get('uuid');
  const testimonialUuid = testimonial.uuid;

  const { shouldHideBonjoroTestimonialBranding } = useFeatures();
  const logoUrl = getTestimonialLogoUrl(
    shouldHideBonjoroTestimonialBranding,
    testimonial?.landing_page?.logo
  );

  const {
    attachments,
    primary_color,
    secondary_color,
    cta_corner_radius,
    font_family
  } = testimonial?.metadata?.style ?? {};

  const bgColor = attachments?.box_color;
  const textColor = computeTextColor(bgColor);

  const { mutateAsync: updateRespondentPositionMutation } = useMutation(
    (updateRespondentPositionParams: UpdateRespondentPositionParams) =>
      updateRespondentPosition(updateRespondentPositionParams)
  );

  const handlePositionChange = useDebouncedCallback(
    (value: string) => {
      if (!value) return;

      setPosition(value);
      updateRespondentPositionMutation({
        testimonialUuid,
        respondentUuid,
        position: value,
      });
    },
    [respondent, requests],
    600
  );

  const handlePreviousClick = () => {
    previousStep();
    onPreviousClick?.();
  };

  const handleNextClick = () => {
    const payload: TestimonialResponseFormRequests = {
      official_title: !!position,
      ...mapRequestsForTracking(requests),
    };
    analytics.track(TESTIMONIAL_RESPONSE_FORM_NEXT, payload);

    nextStep();
    onNextClick?.();
  };

  useEffect(() => {
    if (!isCurrent) return;

    setNavBarProps(false, false, handleNextClick, previousStep);
  }, [isCurrent]);

  const fileListColumn = useMemo(() => {
    if (fileList.isEmpty()) return null;

    return fileList
      .map((request, index) => (
        <RequestFilesItem
          key={index}
          request={request as Request}
          disableFileUpload={disableFileUpload}
          fontColor={textColor}
          fontFamily={font_family}
          ctaColor={secondary_color}
          ctaCornerRadius={cta_corner_radius}
        />
      ))
      .toArray();
  }, [fileList]);

  const linkListColumn = useMemo(() => {
    if (!showPositionField && linkList.isEmpty()) return null;

    return (
      <>
        {showPositionField && (
          <RequestPositionField
            defaultValue={position}
            onChange={handlePositionChange}
            fontColor={textColor}
          />
        )}
        {!linkList.isEmpty() && (
          <div className='mb-5'>
            <span
              className='flex pb-5 text-sm font-bold'
              style={{color: textColor}}
            >
              {t('testimonial:additionalInfo.form.social')}
            </span>
            {linkList
              .map((request, index) => (
                <RequestLinksItem key={index} request={request as Request} />
              ))
              .toArray()}
          </div>
        )}
      </>
    );
  }, [showPositionField, linkList, textColor]);

  const columns = useMemo(
    () => [fileListColumn, linkListColumn].filter((column) => !!column),
    [fileListColumn, linkListColumn]
  );

  return (
    <AttachmentCard
      logoUrl={logoUrl ?? undefined}
      columns={columns}
      buttons={
        <AttachmentCardButtons
          onNextClick={handleNextClick}
          onPreviousClick={handlePreviousClick}
          ctaCornerRadius={cta_corner_radius}
          ctaColor={primary_color}
          fontFamily={font_family}
        />
      }
      className={className}
      emptyStateCopy={emptyStateCopy}
      bgColor={bgColor}
      fontFamily={font_family}
      fontColor={textColor}
    />
  );
}
